import { zodResolver } from "@hookform/resolvers/zod";
import { getAccessProfileQueryOptions } from "@packages/api-client/hooks/authorizationController";
import {
	Action,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	Role,
	type UserAccessProfile,
} from "@packages/shared-types";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Mail, Plus, Search, User } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useInviteStaffMember } from "@/api/staff";
import { PageHeader } from "@/components/PageHeader";
import { RolePermissionsModal } from "@/components/RolePermissionsModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { hasPermission, useHasPermission } from "@/hooks/useAccessControl";
import { ensureQueryDataSurvivingCancel } from "@/lib/ensureQueryDataSurvivingCancel";
import { ForbiddenError } from "@/lib/forbiddenError";
import { OrganizationSettingsNavigation } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationSettingsNavigation";
import {
	getOrganizationRoleIcon,
	getOrganizationRoleLabel,
} from "@/utils/roleUtils";
import {
	EMPTY_STAFF_INVITE,
	type StaffInviteFormValues,
	staffInviteSchema,
	toStaffInvitePayload,
} from "./-components/staffInviteSchema";
import { UnifiedStaffList } from "./-components/UnifiedStaffList";

// Search params schema
const staffSearchSchema = z.object({
	page: z.number().min(1).catch(DEFAULT_PAGE),
	limit: z.number().min(1).max(100).catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
	// Preserve the org-level farm scope param across this route's validation.
	farm: z.string().optional(),
});

const INVITE_FORM_ID = "staff-invite-form";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/staff/")({
	validateSearch: staffSearchSchema,
	loader: async ({ context, params }) => {
		const orgId = params.orgId;

		// Preload access profile
		const profile = await ensureQueryDataSurvivingCancel(
			context.queryClient,
			getAccessProfileQueryOptions({ organizationId: orgId }),
		);

		// Guard: must have VIEW_STAFF permission
		if (!hasPermission(profile as UserAccessProfile, Action.VIEW_STAFF)) {
			throw new ForbiddenError("Access denied to staff page");
		}

		return { orgId };
	},
	component: StaffPage,
});

function StaffPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/staff/",
	});
	const search = Route.useSearch();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 300); // 300ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const checkPermission = useHasPermission();
	const canManageStaff = checkPermission(Action.MANAGE_STAFF);
	const canInviteStaff = checkPermission(Action.INVITE_STAFF);

	// Mutations
	const inviteStaffMutation = useInviteStaffMember();

	// Pagination handlers
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handlePageSizeChange = useCallback((limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	}, []);

	const handleSearchChange = useCallback((newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	}, []);

	const handlePaginationMetaChange = useCallback(
		(meta: typeof paginationMeta) => {
			setPaginationMeta(meta);
		},
		[],
	);

	// Invite staff state
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

	const inviteForm = useForm<StaffInviteFormValues>({
		resolver: zodResolver(staffInviteSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: EMPTY_STAFF_INVITE,
	});

	useEffect(() => {
		if (isInviteDialogOpen) inviteForm.reset(EMPTY_STAFF_INVITE);
	}, [isInviteDialogOpen, inviteForm]);

	const guardedInvite = useGuardedSubmit(inviteForm, (values) =>
		inviteStaffMutation
			.mutateAsync({
				organizationId: orgId,
				data: toStaffInvitePayload(values),
			})
			.then(() => setIsInviteDialogOpen(false)),
	);

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<PageHeader
				title={t("staff.title")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="staff-settings-container"
			>
				{/* Side Navigation */}
				<OrganizationSettingsNavigation activeTab="staff" />

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="staff-main-content-area"
				>
					<Card className="h-full flex flex-col overflow-hidden">
						<CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
							{/* Search */}
							<div className="relative w-full sm:w-72">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/70 h-4 w-4" />
								<Input
									placeholder={t("staff.searchPlaceholder")}
									value={searchTerm}
									onChange={(e) => handleSearchChange(e.target.value)}
									className="pl-10"
									data-form-type="other"
								/>
								{searchTerm !== debouncedSearchTerm && (
									<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
									</div>
								)}
							</div>

							{/* Actions */}
							{canInviteStaff && (
								<>
									<Button
										className="btn btn-primary text-primary-content w-full sm:w-auto"
										onClick={() => setIsInviteDialogOpen(true)}
									>
										<Plus className="h-4 w-4 mr-2" />
										{t("common.inviteNew")}
									</Button>
									<ResponsiveFormDialog
										open={isInviteDialogOpen}
										onOpenChange={setIsInviteDialogOpen}
										pending={inviteStaffMutation.isPending}
										title={t("common.inviteNew")}
										description={t("staff.inviteDescription")}
										footer={
											<>
												<Button
													variant="outline"
													onClick={() => setIsInviteDialogOpen(false)}
													disabled={inviteStaffMutation.isPending}
													className="flex-1 h-11"
												>
													{t("common.cancel")}
												</Button>
												<SubmitButton
													form={INVITE_FORM_ID}
													pending={inviteStaffMutation.isPending}
													pendingLabel={t("common.sending")}
													className="flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-content"
												>
													{t("common.sendInvite")}
												</SubmitButton>
											</>
										}
									>
										<Form {...inviteForm}>
											<form
												id={INVITE_FORM_ID}
												onSubmit={guardedInvite}
												className="space-y-6"
											>
												{/* Contact Information Section */}
												<div className="space-y-4">
													<div className="flex items-center gap-2 pb-2 border-b border-base-200">
														<div className="w-2 h-2 bg-primary rounded-full" />
														<h3 className="text-lg font-medium text-base-content">
															{t("common.contact")}
														</h3>
													</div>
													<div className="grid gap-4">
														<FormField
															control={inviteForm.control}
															name="email"
															render={({ field }) => (
																<FormItem>
																	<FormLabel required>
																		{t("common.email")}
																	</FormLabel>
																	<div className="relative">
																		<Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50 z-10" />
																		<FormControl>
																			<Input
																				{...field}
																				type="email"
																				placeholder={t(
																					"common.emailPlaceholder",
																				)}
																				className="pl-10 h-10"
																			/>
																		</FormControl>
																	</div>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<FormField
															control={inviteForm.control}
															name="inviteeName"
															render={({ field }) => (
																<FormItem>
																	<FormLabel>{t("common.name")}</FormLabel>
																	<div className="relative">
																		<User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50 z-10" />
																		<FormControl>
																			<Input
																				{...field}
																				placeholder={t(
																					"common.namePlaceholder",
																				)}
																				className="pl-10 h-10"
																			/>
																		</FormControl>
																	</div>
																	<FormMessage />
																</FormItem>
															)}
														/>
													</div>
												</div>

												{/* Role & Permissions Section */}
												<div className="space-y-4">
													<div className="flex items-center gap-2 pb-2 border-b border-base-200">
														<div className="w-2 h-2 bg-primary rounded-full" />
														<h3 className="text-lg font-medium text-base-content">
															{t("common.permissions")}
														</h3>
													</div>
													<FormField
														control={inviteForm.control}
														name="role"
														render={({ field }) => (
															<FormItem>
																<FormLabel required>
																	{t("common.organizationRole")}
																</FormLabel>
																<p className="text-xs text-muted-foreground">
																	{t("common.organizationRoleDescription")}
																</p>
																<Select
																	value={field.value}
																	onValueChange={field.onChange}
																>
																	<FormControl>
																		<SelectTrigger className="h-10">
																			<SelectValue
																				placeholder={t(
																					"common.selectOrganizationRole",
																				)}
																			/>
																		</SelectTrigger>
																	</FormControl>
																	<SelectContent>
																		{Object.values(Role)
																			.filter(
																				(role) =>
																					role === Role.ORG_ADMINISTRATOR ||
																					role === Role.ORG_MANAGER ||
																					role ===
																						Role.ORG_MEDICAL_PROFESSIONAL ||
																					role === Role.ORG_ACCOUNTANT ||
																					role === Role.ORG_VIEWER,
																			)
																			.map((role) => (
																				<SelectItem
																					key={role as string}
																					value={role as string}
																				>
																					<div className="flex items-center gap-3">
																						{getOrganizationRoleIcon(role)}
																						<div className="flex flex-col">
																							<span className="font-medium">
																								{getOrganizationRoleLabel(
																									role,
																									t,
																								)}
																							</span>
																							<span className="text-xs text-muted-foreground">
																								{t(
																									`roles.descriptions.${(role as string).toLowerCase()}`,
																								)}
																							</span>
																						</div>
																					</div>
																				</SelectItem>
																			))}
																	</SelectContent>
																</Select>
																<FormMessage />
															</FormItem>
														)}
													/>
													<div className="flex justify-start mt-3">
														<RolePermissionsModal />
													</div>
												</div>
												<FormRootMessage />
											</form>
										</Form>
									</ResponsiveFormDialog>
								</>
							)}
						</CardHeader>
						<CardContent className="flex-1 overflow-hidden flex flex-col space-y-4">
							{/* Pagination */}
							<div className="shrink-0">
								<Pagination
									pagination={paginationMeta}
									onPageChange={handlePageChange}
									onPageSizeChange={handlePageSizeChange}
								/>
							</div>

							{/* Unified Staff and Invitations Table */}
							<div className="flex-1 overflow-hidden">
								<UnifiedStaffList
									organizationId={orgId}
									pagination={{
										page: currentPage,
										limit: pageSize,
									}}
									searchTerm={debouncedSearchTerm}
									canManageStaff={canManageStaff}
									onPaginationMetaChange={handlePaginationMetaChange}
								/>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
